import React, { useEffect, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Flex } from "antd";
import ProgressSegment from "./components/progressSegment/ProgressSegment";
import {
  InterviewSession,
  Question,
  QuestionAnswers,
} from "../../Interface/Index";
import InterviewService from "../../Services/interview.service";
import { DashboardContext } from "../../Context/DashboardContext";
import LoaderContext from "../../Context/LoaderContext/Index";
import ExitFullScreenConfirmation from "./components/modals/ExitFullScreenConfirmation";
import RetryEvaluationModal from "./components/modals/RetryEvaluationModal";
import CamRecorder from "../../Components/CamRecorder/CamRecorder";
import Draggable from "react-draggable";
import AllowCameraConfirmation from "./components/modals/AllowCameraConfirmation";
import WarningMessage from "./components/WarningMessage";
import InterviewRoundContent from "./components/interviewRoundContent/InterviewRoundContent";
import QuestionActionButtons from "./components/interviewRoundContent/QuestionActionButtons";
import usePollingFunctions from "./usePollingFunctions";
import { useInterviewRoundContext } from "../../Context/InterviewRoundContext";
import useInterviewRoundsHandlers from "./useInterviewRoundsHandlers";
import "./index.scss";

const InterviewRounds: React.FC = () => {
  const { sessionId } = useParams();

  const interviewService = useMemo(() => new InterviewService(), []);

  // Dashboard Context values
  const { interviewSessions, appliedJob } = useContext(DashboardContext);

  // Common Loader Context
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  // Polling functions
  const { checkForNextRoundQuestions } = usePollingFunctions();

  // Interview Round Context States And SetStateActions
  const {
    currentSession,
    setCurrentSession,
    currentSessionQuestions,
    setCurrentSessionQuestions,
    currentSessionQuestionAnswers,
    setCurrentSessionQuestionAnswers,
    isRoundFinished,
    setIsRoundFinished,
    showExitConfirmation,
    setShowExitConfirmation,
    exitTimer,
    isFullscreen,
    showRetryEvaluation,
    activeQuestionIndex,
    setActiveQuestionIndex,
    showCamRecorder,
    showCameraConfirmation,
    elementRef,
    setIsFullscreen,
    isVisibilityActive,
    setIsVisibilityActive,
    setExitTimer,
  } = useInterviewRoundContext();

  // Interview Round Handlers
  const {
    handleEvaluation,
    handleCameraAccessConfirmation,
    handleCameraAccessdenied,
    handleCancelCameraAccessConfirmation,
    handleRetryEvaluation,
    handleCancelExit,
    handleConfirmExit,
    handleAnswerSingleQuestion,
    handleBack,
    handleNext,
    handleQuestionAnswerChange,
    handleStartInterview,
    onNavigateToQuestions,
  } = useInterviewRoundsHandlers();

  // Effect to init the current interview round
  useEffect(() => {
    const fetchData = async (
      sessionId: string,
      sessions: InterviewSession[]
    ) => {
      setLoading(true);
      setLoadingMessage("Loading Interview Session, please wait...");
      try {
        const session = sessions.find((session) => session._id === sessionId);
        if (!session) {
          console.error(
            `No session found with the provided id -> ${sessionId}`
          );
          return;
        }

        const sessionQuestions: Question[] = [];

        if (!session.isQuestionsGenerated) {
          setLoadingMessage("Generating questions, please wait...");
          const questions = await checkForNextRoundQuestions(session._id);
          sessionQuestions.push(...questions);
        } else {
          setLoadingMessage("Loading questions, please wait...");
          const questions = await interviewService.getQuestionsBySessionId(
            session._id
          );
          sessionQuestions.push(...questions);
        }

        if (sessionQuestions.length === 0) {
          console.error("No questions found for the session");
          return;
        }

        const formattedAnswers: QuestionAnswers[] = sessionQuestions.map(
          (question: Question) => ({
            questionId: question._id,
            answer: question.responses?.[0]?.description ?? null,
            fileUrl:
              question.type === "fileUpload"
                ? question.responses?.[0]?.fileUrl ?? null
                : null,
          })
        );

        const isRoundCompleted =
          session.status !== "pending" || appliedJob?.status !== "applied";

        if (isRoundCompleted) {
          setActiveQuestionIndex(sessionQuestions.length);
        } else {
          setActiveQuestionIndex(-1);
        }

        setCurrentSession(session);
        setCurrentSessionQuestions(sessionQuestions);
        setCurrentSessionQuestionAnswers(formattedAnswers);
        setIsRoundFinished(isRoundCompleted);
      } catch (error: any) {
        console.error("Error fetching data:", error);
      } finally {
        setTimeout(() => {
          setLoading(false);
          setLoadingMessage(null);
        }, 500);
      }
    };

    if (sessionId && interviewSessions.length > 0) {
      fetchData(sessionId, interviewSessions);
    }
  }, [sessionId, interviewSessions]);

  // Effect to handle full-screen toggle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };
    const handleVisibilityChange = () => {
      setIsVisibilityActive(document.visibilityState === "visible");
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (
      (!isFullscreen || !isVisibilityActive) &&
      activeQuestionIndex >= 0 &&
      currentSession &&
      currentSession.status === "pending" &&
      !exitTimer &&
      !isRoundFinished
    ) {
      setShowExitConfirmation(true);
      setExitTimer(true);
    }
  }, [isFullscreen, isVisibilityActive, isRoundFinished]);

  return (
    <section className="interview_round_main_container">
      <div
        className="interview_round_fullscreen_container"
        ref={elementRef}
        tabIndex={0}
      >
        {/* RETRY EVALUATION MODAL */}
        <RetryEvaluationModal
          containerRef={elementRef}
          showEvaluation={showRetryEvaluation}
          handleRetryEvaluation={handleRetryEvaluation}
        />

        {/* EXIT FULLSCREEN CONFIRMATION MODAL */}
        <ExitFullScreenConfirmation
          handleConfirmExit={handleConfirmExit}
          containerRef={elementRef}
          isFullscreen={isFullscreen}
          showExitConfirmation={showExitConfirmation}
          setShowExitConfirmation={setShowExitConfirmation}
          handleCancelExit={handleCancelExit}
          exitTimer={exitTimer}
        />

        {/* Displaying alert message only if the round is not started or finished */}
        {activeQuestionIndex >= 0 && (
          <WarningMessage isRoundFinished={isRoundFinished} />
        )}

        {/* PROGRESS BAR */}
        {activeQuestionIndex >= 0 && (
          <Flex align="center" justify="center" style={{ width: "100%" }}>
            <ProgressSegment
              totalQuestions={currentSessionQuestions.length}
              currentQuestion={activeQuestionIndex}
              onQuestionClick={handleAnswerSingleQuestion}
              isRoundStarted={activeQuestionIndex > -1}
              isRoundFinished={isRoundFinished}
            />
          </Flex>
        )}

        {/* INTERVIEW ROUND DYNAMIC CONTENT */}
        {currentSession && appliedJob && (
          <InterviewRoundContent
            activeQuestionIndex={activeQuestionIndex}
            currentSession={currentSession}
            currentSessionQuestions={currentSessionQuestions}
            handleStartInterview={handleStartInterview}
            handleBack={handleBack}
            handleNext={handleNext}
            isRoundFinished={isRoundFinished}
            appliedJob={appliedJob}
            handleEvaluation={handleEvaluation}
            currentSessionQuestionAnswers={currentSessionQuestionAnswers}
            onNavigateToQuestions={onNavigateToQuestions}
            handleQuestionAnswerChange={handleQuestionAnswerChange}
          />
        )}

        {/* RENDERING QUESTIONS ACTION BUTTONS */}
        {activeQuestionIndex >= 0 &&
          activeQuestionIndex < currentSessionQuestions.length && (
            <QuestionActionButtons
              activeQuestionIndex={activeQuestionIndex}
              currentSessionQuestionAnswers={currentSessionQuestionAnswers}
              currentSessionQuestions={currentSessionQuestions}
              handleBack={handleBack}
              handleNext={handleNext}
              isRoundFinished={isRoundFinished}
            />
          )}

        {showCamRecorder && (
          <Draggable>
            <div
              className="video-recorder-container"
              style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: 9999,
                cursor: "grab",
              }}
            >
              <CamRecorder
                handleCameraAccessdenied={handleCameraAccessdenied}
              />
            </div>
          </Draggable>
        )}
      </div>

      {/* CAMERA CONFIRMATION MODAL */}
      <AllowCameraConfirmation
        containerRef={elementRef}
        isOpen={showCameraConfirmation}
        handleCancelExit={handleCancelCameraAccessConfirmation}
        handleOk={handleCameraAccessConfirmation}
      />
    </section>
  );
};

export default InterviewRounds;
